import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';

type Item = { to: string; label: string; count?: number; dot?: 'warn' | 'bad' | 'ok' };
type Section = { label: string; items: Item[] };

const SECTIONS: Section[] = [
  {
    label: 'Records',
    items: [
      { to: '/', label: 'Dashboard' },
      { to: '/personal', label: 'Personal details' },
      { to: '/devices', label: 'Devices' },
      { to: '/notifications', label: 'Notifications' },
      { to: '/accounts', label: 'Accounts & cards' },
      { to: '/travel', label: 'Foreign travel' },
      { to: '/vehicles', label: 'Vehicles' },
      { to: '/logins', label: 'Logins & access' },
      { to: '/documents', label: 'Documents' },
    ],
  },
  {
    label: 'Compliance',
    items: [
      { to: '/conditions', label: 'My conditions' },
      { to: '/reminders', label: 'Reminders' },
      { to: '/reconciliation', label: 'Reconciliation' },
      { to: '/police-log', label: 'Police log' },
    ],
  },
  {
    label: 'Evidence & access',
    items: [
      { to: '/verify', label: 'Verify', dot: 'ok' },
      { to: '/reports', label: 'Register & reports' },
      { to: '/grants', label: 'Inspector grants', count: 2 },
      { to: '/inspector', label: 'Inspector preview' },
      { to: '/settings', label: 'Settings' },
    ],
  },
];

export function Sidebar({ open = false, onClose }: { open?: boolean; onClose?: () => void }) {
  const [rem, setRem] = useState<{ count: number; dot: 'warn' | 'bad' } | null>(null);
  const [devCount, setDevCount] = useState<number | null>(null);
  const [stale, setStale] = useState<number | null>(null);
  useEffect(() => {
    fetch('/api/reminders').then((r) => r.json()).then((r) => {
      const c = r.counts;
      if (c && c.actionable > 0) setRem({ count: c.actionable, dot: c.overdue > 0 ? 'bad' : 'warn' });
      else setRem(null);
    }).catch(() => {});
    fetch('/api/devices').then((r) => r.json()).then((r) => setDevCount((r.devices ?? []).length)).catch(() => {});
    fetch('/api/reconcile').then((r) => r.json()).then((r) => setStale(r.buckets?.counts?.stale_open ?? null)).catch(() => {});
  }, []);

  const decorate = (it: Item): Item => {
    if (it.to === '/reminders' && rem) return { ...it, count: rem.count, dot: rem.dot };
    if (it.to === '/devices' && devCount !== null && devCount > 0) return { ...it, count: devCount };
    if (it.to === '/reconciliation' && stale !== null && stale > 0) return { ...it, count: stale, dot: 'bad' };
    return it;
  };

  return (
    <aside className={`sidebar${open ? ' open' : ''}`}>
      <div className="brand">
        <div className="brand-mark">S</div>
        <div>
          <div className="brand-name">SOR</div>
          <div className="brand-sub">Compliance Records</div>
        </div>
        <button className="sidebar-close" type="button" aria-label="Close menu" onClick={onClose}>×</button>
      </div>

      <nav className="nav">
        {SECTIONS.map((section) => (
          <div className="nav-section" key={section.label}>
            <div className="nav-label">{section.label}</div>
            {section.items.map(decorate).map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === '/'}
                onClick={onClose}
                className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              >
                <span className="label">
                  {it.dot && <span className={`dot dot-${it.dot}`} />}
                  {it.label}
                </span>
                {it.count !== undefined && <span className="nav-count">{it.count}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="vault">
        <div className="vault-row">
          <span className="dot dot-ok" style={{ width: 8, height: 8, borderRadius: '50%' }} />
          <div>
            <div className="vault-name">Vault unlocked</div>
            <div className="vault-sub">DEK in memory · L. Ashcroft</div>
          </div>
        </div>
        <button
          className="btn-lock"
          type="button"
          onClick={() => fetch('/api/auth/lock', { method: 'POST' }).then(() => window.location.reload())}
        >
          Lock vault
        </button>
      </div>
    </aside>
  );
}
